import { zodResolver } from "@hookform/resolvers/zod";
import type { PinholeDetailsData, SlotDetailsData } from "@stryker/core/types/compositeSections";
import { useForm } from "react-hook-form";
import { z } from "zod";
import PinholeDiameterImage from "@/assets/images/pinholeDiameter.png";
import SlotDetailsExampleImage from "@/assets/images/slotDetailsExample.png";
import { Button } from "@/components/ui/button";
import { TextInput } from "@/components/ui/textInput";
import { Typography } from "@/components/ui/typography";
import { inputStep, maxInputNumber } from "@/constants/inputs";
import useCUCompositeJobStore from "@/store/useCUCompositeJobStore";
import { getFloat } from "@/utils/mappings";

import ExpandableSection from "../../../../components/job/ExpandableSection";

const cardName = "slotPinholeDetails";
export function SlotPinholeDetails() {
  const { sections, toggleExpandableSection } = useCUCompositeJobStore();
  const isPinhole = sections.slotPinholeDetails?.type === "pinhole";
  const isSlot = sections.slotPinholeDetails?.type === "slot";
  let title = "Slot Details/Pinhole Details";

  if (sections.slotPinholeDetails?.status === "hidden") {
    return null;
  }

  if (isPinhole) {
    title = "Pinhole Details";
  }
  if (isSlot) {
    title = "Slot Details";
  }
  return (
    <ExpandableSection
      status={sections.slotPinholeDetails?.status ?? "hidden"}
      setIsExpanded={() => toggleExpandableSection(cardName)}
      title={title}
    >
      <div>
        {isPinhole && <PinholeDetails />}
        {isSlot && <SlotDetails />}
      </div>
    </ExpandableSection>
  );
}

const slotDetailsSchema = z.object({
  slot_diameter: z.union([
    z.number().lt(maxInputNumber),
    z.string().refine((value) => !isNaN(Number.parseFloat(value)), { message: "Must be a number" }),
  ]),
});

type SlotDetailsSchema = z.infer<typeof slotDetailsSchema>;
function SlotDetails() {
  const { sections, setSlotPinholeDetails } = useCUCompositeJobStore();
  const savedSlotData = sections.slotPinholeDetails?.data as SlotDetailsData | undefined;

  const {
    register,
    formState: { errors },
    handleSubmit,
  } = useForm<SlotDetailsSchema>({
    resolver: zodResolver(slotDetailsSchema),
    values: {
      slot_diameter: savedSlotData?.slot_diameter ?? "",
    },
  });

  return (
    <form
      className="flex flex-col gap-6 md:flex-row md:gap-20"
      onSubmit={handleSubmit((data) => {
        if (!data.slot_diameter) return;
        const slot_diameter =
          typeof data.slot_diameter === "string" ? Number.parseFloat(data.slot_diameter) : data.slot_diameter;
        setSlotPinholeDetails({ data: { slot_diameter }, status: "done" });
      })}
    >
      <TextInput
        className="flex-1 basis-full md:basis-60"
        label="Slot Diameter"
        type="number"
        step={inputStep}
        error={errors.slot_diameter?.message}
        {...register("slot_diameter", { setValueAs: getFloat })}
        endAdornment={
          <Typography variant="text-small" weight="regular" className="text-gray-400">
            Inches
          </Typography>
        }
      />
      <img src={SlotDetailsExampleImage} width="217px" height="161px" alt="Buried example image" />
      <Button className="w-full self-end md:w-fit" type="submit">
        Save
      </Button>
    </form>
  );
}

const pinholeDetailsSchema = z.object({
  pinhole_diameter: z.union([
    z.number().gte(0.1).lt(maxInputNumber),
    z.string().refine((value) => !isNaN(Number.parseFloat(value)), { message: "Must be a number" }),
  ]),
});

type PinholeDetailsSchema = z.infer<typeof pinholeDetailsSchema>;
function PinholeDetails() {
  const { sections, setSlotPinholeDetails } = useCUCompositeJobStore();
  const savedPinholeData = sections.slotPinholeDetails?.data as PinholeDetailsData | undefined;

  const {
    register,
    formState: { errors },
    handleSubmit,
  } = useForm<PinholeDetailsSchema>({
    resolver: zodResolver(pinholeDetailsSchema),
    values: {
      pinhole_diameter: savedPinholeData?.pinhole_diameter ?? "",
    },
  });

  return (
    <form
      className="flex flex-col items-center gap-6 md:flex-row md:gap-20"
      onSubmit={handleSubmit((data) => {
        if (!data.pinhole_diameter) return;
        const pinhole_diameter =
          typeof data.pinhole_diameter === "string" ? Number.parseFloat(data.pinhole_diameter) : data.pinhole_diameter;
        setSlotPinholeDetails({ data: { pinhole_diameter }, status: "done" });
      })}
    >
      <TextInput
        className="flex-1 basis-full md:basis-60"
        label="Pinhole Diameter"
        type="number"
        step={inputStep}
        error={errors.pinhole_diameter?.message}
        {...register("pinhole_diameter", { setValueAs: getFloat })}
        endAdornment={
          <Typography variant="text-small" weight="regular" className="text-gray-400">
            Inches
          </Typography>
        }
      />
      <img src={PinholeDiameterImage} width="217px" height="161px" alt="Buried example image" />
      <Button className="w-full self-end md:w-fit" type="submit">
        Save
      </Button>
    </form>
  );
}
